ioi_partners.ProductView = class {
	/* Options:
		- View Type
		- Products Section Wrapper,
		- Item Group: If its an Item Group page
	*/
	constructor(options) {
		Object.assign(this, options);

		this.make();
		this.bind_mobile_filters_close();

		frappe.call({
			method: "ioi_partners.eshop.filters.get_selected_filters",
		}).then(r => {
			// The preselected family is a merchandising default for a bare visit to
			// /products. It must not override a URL that already says what to show:
			// arriving with ?search=, ?product= (a related-product link) or
			// ?field_filters= (a shared or back-navigated filter) is an explicit
			// request, and this rewrite would replace it with a family derived from the
			// visitor's own subscription.
			//
			// start was already excluded - it means "you are paging through a result
			// set", which is equally explicit. The server side guards on start the
			// same way (filters.py get_family_filters).
			//
			// Skipping this does not write this.field_filters. restore_filters_state()
			// fills it from the URL only when the URL carries field_filters; on a
			// ?search= landing nothing writes it, and bind_filters() defaults it to an
			// empty object so the family click handler has something to mutate.
			// Defaulting it here instead would be race-dependent: bind_filters() runs
			// later, in the product fetch callback, whenever get_selected_filters
			// resolves first, and would overwrite it.
			if (this.has_explicit_route_intent()) return;

			this.field_filters = r.message;
			this.change_route_with_filters({ keep_search: true });
		})
	}

	has_explicit_route_intent() {
		const query_params = frappe.utils.get_query_params();
		// product counts like search. This guard is the ONLY thing that keeps
		// ?product= through the load: it stops the programmatic rewrite, so product
		// needs no keep_search-style flag. It is deliberately absent from
		// change_route_with_filters(), which builds the next URL from a fixed key set
		// - so the first family click drops it, exactly as it drops search. Adding
		// it there would make it survive that click and stick.
		return !!(query_params.start || query_params.search || query_params.product || query_params.field_filters);
	}

	make(from_filters=false) {
		this.products_section.empty();
		this.prepare_toolbar();
		this.get_item_filter_data(from_filters);
	}

	prepare_toolbar() {
		this.products_section.append(`
			<div class="toolbar d-flex">
			</div>
		`);
		this.prepare_search();

		new ioi_partners.ProductSearch({product_view: this});
	}

	// replace: clear the previous listing when the response arrives rather than
	// before the request, so a search re-render does not flash an empty page.
	get_item_filter_data(from_filters=false, { replace = false } = {}) {
		// Get and render all Product related views
		let me = this;
		this.from_filters = from_filters;
		let args = this.get_query_filters();
		// Only the latest request renders: a slow response for "acc" must not
		// overwrite the one for "accou", nor a search the family clicked after it.
		const request_id = this.next_request_id();

		frappe.call({
			method: "ioi_partners.eshop.api.get_product_filter_data",
			args: {
				query_args: args
			},
			callback: function(result) {
				if (request_id !== me.request_id) {
					// The first load is the only call that binds the family filters.
					// Its response can lose to the preselection rewrite that follows it
					// in the constructor (make(true)), which never binds - so the binding
					// still happens when its listing is discarded.
					if (!from_filters) {
						me.bind_filters();
						me.restore_filters_state();
					}
					return;
				}

				if (replace) {
					me.products_section.empty();
				}

				if (!result || result.exc || !result.message || result.message.exc) {
					me.render_no_products_section(true);
				} else {
					// Sub Category results are independent of Items
					if (me.item_group && result.message["sub_categories"].length) {
						me.render_item_sub_categories(result.message["sub_categories"]);
					}

					if (!result.message["items"].length) {
						// if result has no items or result is empty
						me.product_count = 0;
						me.render_no_products_section(false, !!args.search);
					} else {

						// Render views
						me.products = result.message["items"];
						me.render_list_view();
						me.product_count = result.message["items_count"];
					}

					// Bind filter actions
					if (!from_filters) {
						// If `get_product_filter_data` was triggered after checking a filter,
						// don't touch filters unnecessarily, only data must change
						// filter persistence is handle on filter change event
						me.bind_filters();
						me.restore_filters_state();
					}

					// Bottom paging
					me.add_paging_section();

					// Live header: inject count + active family into the server-rendered page-head
					// (products.html). Fires on load and every family change.
					me.update_header();
				}
			}
		});
	}

	next_request_id() {
		this.request_id = (this.request_id || 0) + 1;
		return this.request_id;
	}

	is_search_active() {
		const search = frappe.utils.get_query_params().search;
		// A repeated ?search= arrives as an array, not a string.
		return typeof search === "string" && !!search;
	}

	// Called by search.js with a term of 3+ characters. A search runs on the
	// whole catalogue, so the family pills are cleared to match the results and
	// update_header() drops the family from the page head. The term goes to the
	// URL as ?search=, which is what get_query_filters() sends and what paging
	// and a reload keep. replaceState, not pushState: one history entry per
	// keystroke would make Back unusable.
	search(query) {
		this.field_filters = {};
		this.set_family_checkboxes(this.field_filters);
		this.replace_route({ search: query });
		this.reload_products();
	}

	// Called by search.js when the box is emptied: back to the view of a bare
	// visit, with the preselected families from the server (filters.py), as the
	// constructor does on load.
	clear_search() {
		// Taken now, so a search typed while this resolves wins over it.
		const request_id = this.next_request_id();

		frappe.call({
			method: "ioi_partners.eshop.filters.get_selected_filters",
		}).then(r => {
			if (request_id !== this.request_id) return;

			this.field_filters = r.message || {};
			this.set_family_checkboxes(this.field_filters);
			// Same URL a bare visit ends on (change_route_with_filters).
			this.replace_route({
				start: 0,
				field_filters: JSON.stringify(this.if_key_exists(this.field_filters)),
			});
			this.reload_products();
		});
	}

	// Re-render the listing only. make() would also rebuild #search-box
	// (prepare_search), dropping the focus and the text being typed.
	reload_products() {
		this.get_item_filter_data(true, { replace: true });
	}

	replace_route(params) {
		// Writes only the given keys, like change_route_with_filters().
		window.history.replaceState('filters', '', `${location.pathname}?` + this.get_query_string(params));
	}

	set_family_checkboxes(field_filters) {
		const families = (field_filters && field_filters['product_family']) || [];
		$('.field-filter[data-filter-name="product_family"]').each(function () {
			$(this).prop('checked', families.includes(this.dataset.filterValue));
		});
		this.sync_active_families();
	}

	render_list_view() {
		this.prepare_product_area_wrapper("list");

		new ioi_partners.ProductList({
			items: this.products,
			products_section: $("#products-list-area"),
			product_listing: this.product_listing
		});
	}

	update_header() {
		// Inject only DATA into the server-rendered page-head (products.html): the product
		// count and the active family name. Translated labels ("Products", "products",
		// "family") stay in the Jinja _() markup — nothing translated is built here.
		$('.ph-count').text(this.product_count != null ? this.product_count : 0);

		const families = (this.field_filters && this.field_filters['product_family']) || [];
		const family = families.length ? families[0] : null;
		// .ptf-* is the <768px filter toggler (products.html). It REUSES the derivation above
		// rather than recomputing one. Hooked here and not in sync_active_families() because
		// that one hard-returns below 768px (views.js:371) — exactly where the toggler exists —
		// and only mirrors :checked onto a class, never resolving the family name. update_header()
		// runs from the get_item_filter_data() callback (views.js:86) on load and on every family
		// change, so the button label cannot go stale.
		if (family) {
			$('.ph-family, .ptf-family').text(__(family)); // family docname (data); __() mirrors the sidebar's _() display
			$('.ph-family-sep, .ptf-family-sep').show();
		} else {
			$('.ph-family-sep, .ptf-family-sep').hide();   // no-family state (e.g. ?start pagination, filters.py:28)
		}

		// The description of that family, under the count line: products.html renders one per
		// sidebar family that has one, all hidden, and only which one shows changes here. The
		// docname is matched exactly, as set_family_checkboxes() does; String() because .data()
		// (the family click handler) turns a docname that looks like a number into one. The
		// hidden property, never .show()/.hide(): [hidden] is display: none !important.
		$('.page-head__family-description').each(function () {
			this.hidden = !family || this.dataset.family !== String(family);
		});
	}

	prepare_product_area_wrapper(view) {
		let left_margin = view == "list" ? "ml-2" : "";
		let top_margin = view == "list" ? "mt-6" : "mt-minus-1";
		return this.products_section.append(`
			<br>
			<div id="products-${view}-area" class="row products-list ${ top_margin } ${ left_margin }"></div>
		`);
	}

	get_query_filters() {
		const filters = frappe.utils.get_query_params();
		let {field_filters, attribute_filters} = filters;

		// typeof guard because get_query_params() turns a repeated key into an array.
		const search = typeof filters.search === "string" && filters.search ? filters.search : null;

		// A search covers the whole catalogue: a family in the URL is ignored while
		// one is active, as search() also clears the pills.
		field_filters = field_filters && !search ? JSON.parse(field_filters) : {};
		attribute_filters = attribute_filters ? JSON.parse(attribute_filters) : {};

		return {
			field_filters: field_filters,
			attribute_filters: attribute_filters,
			item_group: this.item_group,
			search: search,
			// Exact product code from a related-product link; api.py resolves it to a
			// single product_id match and lets it win over search.
			product: typeof filters.product === "string" ? filters.product : null,
			start: filters.start || null,
			from_filters: this.from_filters || false
		};
	}

	add_paging_section() {
		$(".product-paging-area").remove();

		if (!this.products) { return; }

		const query_params = frappe.utils.get_query_params();
		const start = Math.max(0, cint(query_params.start)); // parse number and clamp to positive
		const page_length = 20;

		const has_prev_btn = start > 0;
		const has_next_btn = this.product_count > page_length;
		const has_buttons = has_prev_btn || has_next_btn;

		if (!has_buttons) { return; }

		const paging = $('<div class="product-paging-area mt-5">')
			.appendTo(this.products_section);

		if (has_prev_btn) {
			const prev_page = Math.max(start - page_length, 0) // clamp to positive number
			const prev = $('<button class="btn btn-default btn-prev">')
				.attr("data-start", prev_page)
				.text(__("Previous"))
			paging.append(prev);
		}

		if (has_next_btn) {
			const next_page = start + page_length
			const next = $('<button class="btn btn-default btn-next" style="float: right">')
				.attr("data-start", next_page)
				.text(__("Next"))
			paging.append(next);
		}

		this.bind_paging_action();
	}

	prepare_search() {
		const $target = $(".eshop-navbar__search").length ? $(".eshop-navbar__search") : $(".toolbar");
		$target.empty(); // idempotent: the navbar mount is persistent (server-rendered), unlike the
		// old body .toolbar that make()→products_section.empty() re-created fresh. Clear before
		// re-appending so each make() (bootstrap ×2 + every family click) leaves exactly ONE
		// #search-box. No-op for the .toolbar fallback.
		// One translated string, escaped once, is both the placeholder and the accessible name:
		// quoted, so every word of it reaches the attribute, not only the first.
		const label = frappe.utils.escape_html(__("Search for Products"));
		$target.append(`
			<div class="input-group col-8 p-0">
				<div class="dropdown w-100" id="dropdownMenuSearch">
					<input type="search" name="query" id="search-box" class="form-control font-md"
						placeholder="${label}"
						aria-label="${label}" autocomplete="off">
					<div class="search-icon">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor" stroke-width="2" stroke-linecap="round"
							stroke-linejoin="round"
							class="feather feather-search">
							<circle cx="11" cy="11" r="8"></circle>
							<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
						</svg>
					</div>
					<!-- Results render in the product grid (ProductView.search) -->
				</div>
			</div>
		`);
	}

	bind_paging_action() {
		let me = this;
		$('.btn-prev, .btn-next').click((e) => {
			const $btn = $(e.target);
			me.from_filters = false;

			$btn.prop('disabled', true);
			const start = $btn.data('start');

			let query_params = frappe.utils.get_query_params();
			query_params.start = start;
			let path = window.location.pathname + '?' + frappe.utils.get_url_from_dict(query_params);
			window.location.href = path;
		});
	}


	bind_filters() {
		let me = this;
		// Keep whatever is already here, and default to an empty object otherwise.
		// This used to read this.default_field_filters, which nothing ever defines,
		// so it reset field_filters to undefined on every load. The change handler
		// below mutates it in place, so from any landing that did not write it back
		// (a ?search= or a bare ?start=N) the first family click threw a TypeError
		// and did nothing.
		this.field_filters = this.field_filters || {};
		this.attribute_filters = {};

		$('.product-filter').on('change', (e) => {
			me.from_filters = true;
			const $checkbox = $(e.target);

			if ($checkbox.is('.field-filter')) {
				const {
					filterName: filter_name,
					filterValue: filter_value
				} = $checkbox.data();

				me.field_filters[filter_name] = []

				$(".field-filter").each(function(index) {
					const {
						filterName: family_name,
						filterValue: family_value
					} = $(this).data();

					if ($(this).is(':checked')) {
						if (!in_list(me.field_filters[filter_name], family_value)) {
							me.field_filters[filter_name].push(family_value);
						}
					}
				})

				if (this.field_filters[filter_name].length === 0) {
					delete this.field_filters[filter_name];
				}
			}

			me.change_route_with_filters();
			me.sync_active_families();
		});

		// bind filter lookup input box
		$('.filter-lookup-input').on('keydown', frappe.utils.debounce((e) => {
			const $input = $(e.target);
			const keyword = ($input.val() || '').toLowerCase();
			const $filter_options = $input.next('.filter-options');

			$filter_options.find('.filter-lookup-wrapper').show();
			$filter_options.find('.filter-lookup-wrapper').each((i, el) => {
				const $el = $(el);
				const value = __($el.data('value')).toLowerCase();
				if (!value.includes(keyword)) {
					$el.hide();
				}
			});
		}, 300));
	}

	bind_mobile_filters_close() {
		// <768px: collapse the family panel once a family is picked — with 18+ families an
		// open panel buries the product grid.
		//
		// DELEGATED on document and bound from the constructor (which runs exactly once,
		// products.js:12), so bind_filters()'s once-guard (views.js:73-79) and its DIRECT
		// $('.product-filter').on('change') binding (views.js:236) are untouched. This is a
		// separate subscriber that fires on bubble, i.e. after that handler has already run
		// change_route_with_filters(); collapsing is purely visual and cannot race it.
		//
		// Namespaced + .off() first so a second ProductView instantiation can never stack
		// duplicate collapse('hide') calls on document. The namespace also keeps the teardown
		// surgical — it can never detach the direct .product-filter handler bind_filters() owns.
		//
		// Viewport-gated at call time, not bind time, so crossing 768px needs no rebinding —
		// and nothing ever runs at >=768px, where d-md-block would keep the panel visible
		// anyway while Bootstrap's hide() briefly wrote an inline height:0 onto it.
		// $() on an absent #product-filters (item_group.html reuses .product-filter) is a no-op.
		$(document).off('change.ptfclose').on('change.ptfclose', '.product-filter', () => {
			if (!window.matchMedia('(max-width: 767px)').matches) return;
			$('#product-filters').collapse('hide');
		});
	}

	// keep_search is passed only by the programmatic rewrite in the constructor.
	// The filter-change handler leaves it false on purpose: clicking a family
	// SHOULD clear an active search, which is what makes ?search= self-clearing
	// rather than sticky. The two callers are distinguishable, so the distinction
	// is made by the caller rather than guessed here.
	change_route_with_filters({ keep_search = false } = {}) {
		let route_params = frappe.utils.get_query_params();

		let start = this.if_key_exists(route_params.start) || 0;
		if (this.from_filters) {
			start = 0; // show items from first page if new filters are triggered
		}

		// A repeated ?search= arrives as an array, not a string.
		const search = keep_search && typeof route_params.search === "string"
			? route_params.search
			: null;

		const query_string = this.get_query_string({
			start: start,
			// Carried through so a rewrite the visitor did not ask for cannot drop a
			// parameter they did. get_query_string() skips falsy values, so a null
			// here simply omits the key.
			search: search,
			field_filters: JSON.stringify(this.if_key_exists(this.field_filters)),
			attribute_filters: JSON.stringify(this.if_key_exists(this.attribute_filters)),
		});
		window.history.pushState('filters', '', `${location.pathname}?` + query_string);

		$('.page_content input').prop('disabled', true);

		this.make(true);
		$('.page_content input').prop('disabled', false);
	}

	restore_filters_state() {
		const filters = frappe.utils.get_query_params();
		let {field_filters, attribute_filters} = filters;

		// Not applied during a search (get_query_filters), so not shown either.
		if (field_filters && !this.is_search_active()) {
			field_filters = JSON.parse(field_filters);
			for (let fieldname in field_filters) {
				const values = field_filters[fieldname];
				const selector = values.map(value => {
					return `input[data-filter-name="${fieldname}"][data-filter-value="${value}"]`;
				}).join(',');
				$(selector).prop('checked', true);
			}
			this.field_filters = field_filters;
		}
		if (attribute_filters) {
			attribute_filters = JSON.parse(attribute_filters);
			for (let attribute in attribute_filters) {
				const values = attribute_filters[attribute];
				const selector = values.map(value => {
					return `input[data-attribute-name="${attribute}"][data-attribute-value="${value}"]`;
				}).join(',');
				$(selector).prop('checked', true);
			}
			this.attribute_filters = attribute_filters;
		}

		this.sync_active_families();
	}

	sync_active_families() {
		// :has() fallback for browsers without support (e.g. Firefox ESR): mirror the native
		// :checked state onto an .is-active class on each family <label>, so the scoped
		// active-pill style still applies. Page- + desktop-gated to match the CSS
		// (body.product-page #product-filters, @media min-width:768px); the mobile accordion
		// (collapse/d-md-block on #product-filters) is untouched. Called from
		// restore_filters_state() (initial load + query-param restore) and from the
		// .product-filter change handler in bind_filters() (click). No template/markup change.
		if (!document.body.classList.contains('product-page')) return;
		if (!window.matchMedia('(min-width: 768px)').matches) return;
		document.querySelectorAll('#product-filters .filter-lookup-wrapper label').forEach((label) => {
			const input = label.querySelector('input.product-filter');
			label.classList.toggle('is-active', !!(input && input.checked));
		});
	}

	render_no_products_section(error=false, searching=false) {
		let error_section = `
			<div class="mt-4 w-100 alert alert-error font-md">
				Something went wrong. Please refresh or contact us.
			</div>
		`;
		// The "already subscribed" wording below explains an empty family, not a
		// search that matched nothing.
		let no_search_results_section = `
			<div class="cart-empty frappe-card mt-4">
				<div class="cart-empty-message mt-4">${ __('No products match your search') }</div>
			</div>
		`;
		if (!error && searching) {
			this.products_section.html(no_search_results_section);
			return;
		}

		let no_results_section = `
			<div class="cart-empty frappe-card mt-4">
				<div class="cart-empty-state">
					<img src="/assets/erpnext/images/ui-states/cart-empty-state.png" alt="Empty Cart">
				</div>
				<div class="cart-empty-message mt-4">${ __('You have already subscribed to all products of this classification') }</p>
			</div>
		`;

		this.products_section.html(error ? error_section : no_results_section);
	}

	render_item_sub_categories(categories) {
		if (categories && categories.length) {
			let sub_group_html = `
				<div class="sub-category-container scroll-categories">
			`;

			categories.forEach(category => {
				sub_group_html += `
					<a href="/${ category.route || '#' }" style="text-decoration: none;">
						<div class="category-pill">
							${ category.name }
						</div>
					</a>
				`;
			});
			sub_group_html += `</div>`;

			$("#product-listing").prepend(sub_group_html);
		}
	}

	get_query_string(object) {
		const url = new URLSearchParams();
		for (let key in object) {
			const value = object[key];
			if (value) {
				url.append(key, value);
			}
		}
		return url.toString();
	}

	if_key_exists(obj) {
		let exists = false;
		for (let key in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key]) {
				exists = true;
				break;
			}
		}
		return exists ? obj : undefined;
	}
};